import React, { useEffect, useMemo, useRef, useState } from 'react';

import { createPluginRegistration } from '@embedpdf/core';
import { EmbedPDF } from '@embedpdf/core/react';
import { usePdfiumEngine } from '@embedpdf/engines/react';
import { DocumentManagerPluginPackage } from '@embedpdf/plugin-document-manager/react';
import { InteractionManagerPluginPackage } from '@embedpdf/plugin-interaction-manager/react';
import { RenderPluginPackage } from '@embedpdf/plugin-render/react';
import { ScrollPluginPackage } from '@embedpdf/plugin-scroll/react';
import { SelectionPluginPackage } from '@embedpdf/plugin-selection/react';
import { ViewportPluginPackage } from '@embedpdf/plugin-viewport/react';

import PDFContent from './PDFContent';
import { s3Client } from '../../../api/config';

/**
 * PDFViewer
 *
 * Renders a read-only PDF viewer. Allows viewing, searching, and scrolling.
 * Downloading, printing, and text-copying are restricted at both the
 * permission and browser-event level.
 *
 * @param {object}  props
 * @param {string}  props.pdfUrl           - Presigned S3 URL (or any public URL) of the PDF
 * @param {string}  [props.height]         - CSS height of the viewer container
 */
export default function PDFViewer({ pdfUrl, height = 'clamp(400px, 70vh, 1200px)' }) {
  const { engine, isLoading: engineLoading } = usePdfiumEngine();
  const containerRef = useRef(null);
  const [pdfData, setPdfData] = useState(null);
  const [pdfFetchError, setPdfFetchError] = useState(false);
  const [containerWidth, setContainerWidth] = useState(null);

  // Measure container width so we can scale the PDF to fill it
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      if (w > 0) setContainerWidth(w);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!pdfUrl) return;
    setPdfData(null);
    setPdfFetchError(false);
    s3Client
      .get(pdfUrl, { responseType: 'arraybuffer' })
      .then((res) => setPdfData(res.data))
      .catch(() => setPdfFetchError(true));
  }, [pdfUrl]);

  // Block keyboard shortcuts that could trigger browser save / print dialogs
  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (['s', 'p', 'S', 'P'].includes(e.key)) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };
    const el = containerRef.current;
    el?.addEventListener('keydown', handler, { capture: true });
    return () => el?.removeEventListener('keydown', handler, { capture: true });
  }, []);

  const plugins = useMemo(
    () => {
      if (!pdfData || !containerWidth) return null;
      // Scale to fill the container width. 612pt is a standard letter-page width.
      const scale = containerWidth / 715;
      return [
        createPluginRegistration(DocumentManagerPluginPackage, {
          initialDocuments: [{ buffer: pdfData, name: pdfUrl.split('/').pop() || 'document.pdf', scale }],
          permissions: {
            enforceDocumentPermissions: false,
            overrides: {
              print: false,
              printHighQuality: false,
              copyContents: false,
              modifyContents: false,
              modifyAnnotations: false,
              assembleDocument: false,
            },
          },
        }),
        createPluginRegistration(ViewportPluginPackage, { viewportGap: 0 }),
        createPluginRegistration(ScrollPluginPackage),
        createPluginRegistration(RenderPluginPackage),
        createPluginRegistration(InteractionManagerPluginPackage),
        createPluginRegistration(SelectionPluginPackage),
      ];
    },
    [pdfData, containerWidth],
  );

  const overlayStyle = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text)',
  };

  const isLoading = engineLoading || !engine || !plugins;

  return (
    <div
      ref={containerRef}
      className="pdf-viewer"
      tabIndex={-1}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        height,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-card, 12px)',
        background: 'var(--color-bg-card)',
        boxShadow: 'var(--shadow-sm)',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
      }}
    >
      {pdfFetchError && (
        <div style={{ ...overlayStyle, color: '#dc3545' }}>Failed to load document.</div>
      )}
      {!pdfFetchError && isLoading && (
        <div style={{ ...overlayStyle, opacity: 0.6 }}>Loading PDF engine…</div>
      )}
      {!pdfFetchError && !isLoading && (
        <EmbedPDF engine={engine} plugins={plugins}>
          {({ activeDocumentId }) =>
            activeDocumentId ? (
              <PDFContent documentId={activeDocumentId} engine={engine} />
            ) : (
              <div style={{ ...overlayStyle, opacity: 0.6 }}>Opening document…</div>
            )
          }
        </EmbedPDF>
      )}
    </div>
  );
}
